import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatRp, formatDateID } from "@/lib/constants";
import { X, FileXls, CaretLeft, CaretRight } from "@phosphor-icons/react";

export default function OmzetDrilldown({ open, card, cardLabel, cardValue, isCount, params, onClose, onRowClick }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!open || !card) return;
    setLoading(true);
    try {
      const r = await api.get("/omzet/drilldown", { params: { ...params, card, page, page_size: 25 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat rincian");
    } finally { setLoading(false); }
  }, [open, card, page, params]);

  useEffect(() => { setPage(1); }, [card, open]);
  useEffect(() => { load(); }, [load]);

  const exportXls = async () => {
    try {
      const r = await api.get("/omzet/drilldown/export", { params: { ...params, card }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data);
      a.download = `Drilldown_${card}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch { toast.error("Gagal mengekspor"); }
  };

  if (!open) return null;

  const rows = data?.rows || [];
  const matches = data && !isCount && Math.round(data.total_value) === Math.round(cardValue);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="omzet-drilldown">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="drilldown-backdrop" />
      <div className="relative flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl sm:w-[92vw]">
        <div className="flex items-start justify-between border-b border-slate-100 p-4">
          <div>
            <p className="font-heading text-lg font-extrabold text-slate-800">{cardLabel}</p>
            <p className="text-xs text-slate-500">
              {data ? (<>{data.total_rows} baris · Total <span className="font-bold text-[#6A38C7]">{isCount ? data.total_rows : formatRp(data.total_value)}</span>
                {!isCount && (matches ? <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-testid="drilldown-match">= kartu {formatRp(cardValue)} ✓</span>
                  : <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700" data-testid="drilldown-mismatch">≠ kartu {formatRp(cardValue)}</span>)}</>) : "Memuat…"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="drilldown-export" onClick={exportXls} className="flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-800"><FileXls size={14} /> Excel</button>
            <button data-testid="drilldown-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400" data-testid="drilldown-empty">Tidak ada transaksi pada filter ini.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#F7F0FF] text-left font-bold uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2">Tanggal</th><th className="px-2 py-2">Kode</th><th className="px-2 py-2">Pasien</th>
                  <th className="px-2 py-2">Layanan</th><th className="px-2 py-2">Unit</th><th className="px-2 py-2">Petugas</th>
                  <th className="px-2 py-2 text-right">Tagihan</th><th className="px-2 py-2 text-right">Diterima</th>
                  <th className="px-2 py-2 text-right">Refund</th><th className="px-2 py-2 text-right">Bersih</th>
                  <th className="px-2 py-2 text-right">Sisa</th><th className="px-2 py-2">Metode</th><th className="px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id + "-" + i} data-testid={`drilldown-row-${r.id}`} onClick={() => onRowClick && onRowClick(r)}
                      className="cursor-pointer border-b border-slate-50 hover:bg-rose-50/40">
                    <td className="px-2 py-2 whitespace-nowrap">{r.tanggal ? formatDateID(r.tanggal) : "-"}</td>
                    <td className="px-2 py-2 font-mono text-[10px]">{r.kode}</td>
                    <td className="px-2 py-2 font-semibold text-slate-700">{r.pasien}</td>
                    <td className="px-2 py-2">{r.layanan}</td>
                    <td className="px-2 py-2">{r.unit}</td>
                    <td className="px-2 py-2">{r.petugas}</td>
                    <td className="px-2 py-2 text-right">{formatRp(r.total_tagihan)}</td>
                    <td className="px-2 py-2 text-right text-emerald-700">{formatRp(r.diterima)}</td>
                    <td className="px-2 py-2 text-right text-rose-600">{r.refund ? formatRp(r.refund) : "-"}</td>
                    <td className="px-2 py-2 text-right font-bold">{formatRp(r.pembayaran_bersih)}</td>
                    <td className="px-2 py-2 text-right text-amber-700">{formatRp(r.sisa_tagihan)}</td>
                    <td className="px-2 py-2">{r.metode}</td>
                    <td className="px-2 py-2">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-100 p-3 text-xs">
            <span className="text-slate-500">Halaman {data.page} / {data.pages}</span>
            <div className="flex gap-2">
              <button data-testid="drilldown-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-40"><CaretLeft size={12} /> Sebelumnya</button>
              <button data-testid="drilldown-next" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-40">Berikutnya <CaretRight size={12} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
